/**
 * Copyright 2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect, useMemo, useState } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { Else, If, Then } from "react-if";
import { Modal, theme } from "@nordcloud/gnui";
import { GroupIdentifiers } from "~/generated/graphql";
import { NoData } from "~/components";
import { Pagination } from "~/components/Pagination/Pagination";
import { SearchInput } from "~/components/SearchInput/SearchInput";
import { ModalContent } from "~/components/Styled";
import { TableWrapper } from "~/components/Tables";
import { NameWithLink } from "~/components/Tables/cells/NameWithLink";
import { FlexColumn } from "~/components/Tables/styles";
import { ROUTES } from "~/routing/routes";
import { isEmpty } from "~/tools";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  groups: GroupIdentifiers[];
};

const RESOURCES_PER_PAGE = 15;
const COLUMNS = [
  createColumnHelper<GroupIdentifiers>().accessor((row) => row?.name, {
    header: "Resource Group Name",
    cell: ({ row }) => (
      <NameWithLink
        name={row.original.name}
        link={ROUTES.resourceGroups.generateDetails(row.original.id)}
      />
    ),
    meta: FlexColumn,
  }),
] as never;

export function ResourceGroupsModal({ isOpen, onClose, groups }: Props) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (!isOpen) {
      setSearch("");
      setPage(1);
    }
  }, [isOpen]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const searchedGroups = useMemo(
    () =>
      groups.filter((group) =>
        group.name.toLowerCase().includes(search.toLowerCase())
      ),
    [groups, search]
  );

  const paginatedGroups = useMemo(
    () =>
      searchedGroups.slice(
        (page - 1) * RESOURCES_PER_PAGE,
        page * RESOURCES_PER_PAGE
      ),
    [searchedGroups, page]
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      contentLabel={`In ${groups.length} Resource Groups`}
      contentMaxHeight={"85vh"}
    >
      <SearchInput
        onChange={setSearch}
        onSubmit={() => {}}
        placeholder="Search"
        css={{ paddingBottom: theme.spacing.spacing04 }}
      />
      <ModalContent
        css={{
          marginTop: theme.spacing.spacing02,
          minHeight: isEmpty(searchedGroups) ? "calc(40vh + 60px)" : "40vh",
          minWidth: "630px",
        }}
      >
        <If condition={isEmpty(searchedGroups)}>
          <Then>
            <NoData hasIcon message={"There are no Groups."} />
          </Then>
          <Else>
            <TableWrapper<GroupIdentifiers>
              loading={false}
              data={paginatedGroups}
              columns={COLUMNS}
              rowSelection={{}}
              setRowSelection={() => {}}
              stripped
            />
          </Else>
        </If>
      </ModalContent>

      <Pagination
        count={searchedGroups.length}
        page={page}
        limit={RESOURCES_PER_PAGE}
        onPageChange={setPage}
        small
      />
    </Modal>
  );
}
